/*
  KTC native cart drawer recommendations.

  Contract with the base theme
  ----------------------------
  * Reads only. Nothing here mutates cart items, discounts, notes, the subtotal,
    the terms checkbox or the checkout form. The one write it performs is a
    POST to the locale-aware Ajax cart-add route, which is what the theme's own
    add-to-cart does, so Shopify's cart and pixel events stay intact.
  * After a successful add it re-broadcasts the theme's own `cart:build` and
    `cart:updated` events so theme.js rebuilds the cart markup and the bubble
    count exactly as it does for a quantity change.
  * Every failure path is silent in the UI: the cart, discounts, notes, terms,
    checkout and analytics keep working and nothing resizes. The only signal is
    the polite live region, which sits outside the collapsible body so it is
    announced even when the block itself has nothing to show.

  Recommendation source
  ---------------------
  Shopify's product recommendations API. Curated `complementary` first, then
  `related` for catalogue coverage, deduped, filtered against the live cart, and
  capped at the configured limit.
*/
(function () {
  'use strict';

  var TAG = 'ktc-cart-recommendations';

  if (typeof customElements === 'undefined' || customElements.get(TAG)) {
    return;
  }

  var MAX_API_LIMIT = 10;
  var DEFAULT_LIMIT = 4;
  var REFRESH_DEBOUNCE_MS = 150;
  var IMAGE_PIXELS = 112;
  var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])';

  /* ----------------------------------------------------------------- helpers */

  function themeRoute(name) {
    var root = typeof window !== 'undefined' ? window.theme : null;
    return (root && root.routes && root.routes[name]) || '';
  }

  function ajaxUrl(candidate, suffix, fallback) {
    var url = String(candidate || '').trim() || fallback;
    url = url.replace(/\/+$/, '');
    if (url.slice(-suffix.length) === suffix) {
      return url;
    }
    return url + suffix;
  }

  function withParams(base, params) {
    var query = Object.keys(params)
      .filter(function (key) {
        return params[key] !== null && params[key] !== undefined && params[key] !== '';
      })
      .map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
      })
      .join('&');

    if (!query) {
      return base;
    }
    return base + (base.indexOf('?') === -1 ? '?' : '&') + query;
  }

  function fallbackMoney(cents, format) {
    var raw = cents;
    if (typeof raw === 'string') {
      raw = raw.replace('.', '');
    }
    var amount = Number(raw);
    if (!isFinite(amount)) {
      amount = 0;
    }
    var noDecimals = /amount_no_decimals/.test(String(format || ''));
    var body = noDecimals ? String(Math.round(amount / 100)) : (amount / 100).toFixed(2);
    var parts = body.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    body = parts.join('.');
    if (!format) {
      return '$' + body;
    }
    return String(format).replace(/\{\{\s*\w+\s*\}\}/, body);
  }

  function formatMoney(cents, format) {
    var root = typeof window !== 'undefined' ? window.theme : null;
    if (root && root.Currency && typeof root.Currency.formatMoney === 'function') {
      try {
        var formatted = root.Currency.formatMoney(cents, format);
        if (typeof formatted === 'string' && formatted) {
          return formatted;
        }
      } catch (error) {
        /* fall through to the local formatter */
      }
    }
    return fallbackMoney(cents, format);
  }

  function sizedImage(url, width) {
    if (!url) {
      return '';
    }
    var src = String(url);
    if (src.indexOf('//') === 0) {
      src = 'https:' + src;
    }
    try {
      var base = (typeof window !== 'undefined' && window.location && window.location.href)
        ? window.location.href
        : 'https://shop.invalid/';
      var parsed = new URL(src, base);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return src;
    }
  }

  function imageOf(product) {
    var featured = product.featured_image;
    if (featured && typeof featured === 'object') {
      return { src: featured.src || '', alt: featured.alt || '' };
    }
    if (typeof featured === 'string' && featured) {
      var media = Array.isArray(product.media) ? product.media[0] : null;
      return { src: featured, alt: (media && media.alt) || '' };
    }
    var images = Array.isArray(product.images) ? product.images : [];
    if (images.length) {
      var first = images[0];
      if (typeof first === 'string') {
        return { src: first, alt: '' };
      }
      if (first && typeof first === 'object') {
        return { src: first.src || '', alt: first.alt || '' };
      }
    }
    return { src: '', alt: '' };
  }

  function fill(template, token, value) {
    return String(template || '').split(token).join(value);
  }

  function element(tag, attributes, text) {
    var node = document.createElement(tag);
    if (attributes) {
      Object.keys(attributes).forEach(function (name) {
        var value = attributes[name];
        if (value === null || value === undefined || value === false) {
          return;
        }
        node.setAttribute(name, value === true ? '' : String(value));
      });
    }
    if (text !== null && text !== undefined) {
      node.textContent = String(text);
    }
    return node;
  }

  function replaceChildren(parent, children) {
    while (parent.firstChild) {
      parent.removeChild(parent.firstChild);
    }
    children.forEach(function (child) {
      parent.appendChild(child);
    });
  }

  function isVisible(node) {
    if (!node) {
      return false;
    }
    if (node.hasAttribute && node.hasAttribute('hidden')) {
      return false;
    }
    if (typeof node.offsetParent !== 'undefined' && node.offsetParent === null) {
      var rects = node.getClientRects ? node.getClientRects() : null;
      if (!rects || !rects.length) {
        return false;
      }
    }
    return true;
  }

  function numericList(value) {
    return String(value || '')
      .split(',')
      .map(function (entry) {
        return Number(String(entry).trim());
      })
      .filter(function (entry) {
        return isFinite(entry) && entry > 0;
      });
  }

  /* ------------------------------------------------------------- the element */

  class KtcCartRecommendations extends HTMLElement {
    constructor() {
      super();

      this.bound = false;
      this.generation = 0;
      this.cache = new Map();
      this.refreshTimer = null;
      this.listenerController = null;
      this.requestController = null;
      this.lastAddedProductId = null;
      this.announcedOnce = false;
      this.restoreProductId = null;
      this.hadFocus = false;

      this.onCartEvent = this.onCartEvent.bind(this);
      this.onProductAdded = this.onProductAdded.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onDrawerKeydownCapture = this.onDrawerKeydownCapture.bind(this);
    }

    connectedCallback() {
      // Repeated initialisation (Shopify section re-render, a second script tag,
      // a moved node) must never stack a second set of handlers.
      if (this.bound) {
        return;
      }

      this.bodyEl = this.querySelector('[data-ktc-rec-body]');
      this.listEl = this.querySelector('[data-ktc-rec-list]');
      this.titleEl = this.querySelector('[data-ktc-rec-title]');
      this.statusEl = this.querySelector('[data-ktc-rec-status]');

      if (!this.bodyEl || !this.listEl) {
        return;
      }

      this.bound = true;

      var data = this.dataset;
      var parsedLimit = parseInt(data.limit, 10);
      this.limit = Math.max(1, Math.min(MAX_API_LIMIT, isFinite(parsedLimit) ? parsedLimit : DEFAULT_LIMIT));
      this.moneyFormat = data.moneyFormat || (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '';

      this.recommendationsUrl = ajaxUrl(data.recommendationsUrl, '.json', '/recommendations/products');
      this.cartUrl = ajaxUrl(data.cartUrl || themeRoute('cart'), '.js', '/cart');
      this.cartAddUrl = ajaxUrl(data.cartAddUrl || themeRoute('cartAdd'), '.js', '/cart/add');

      this.labels = {
        add: data.labelAdd || 'Add',
        addAria: data.labelAddAria || 'Add [title] to cart',
        options: data.labelOptions || 'Options',
        optionsAria: data.labelOptionsAria || 'Select options for [title]',
        from: data.labelFrom || 'From',
        available: data.labelAvailable || '[count] suggestions available.',
        added: data.labelAdded || '[title] added to your cart.',
        addFailed: data.labelAddFailed || 'That product could not be added.',
        unavailable: data.labelUnavailable || 'Suggestions are unavailable right now.'
      };

      this.listenerController = new AbortController();
      var options = { signal: this.listenerController.signal };

      document.addEventListener('cart:updated', this.onCartEvent, options);
      document.addEventListener('cart:build', this.onCartEvent, options);
      document.addEventListener('ajaxProduct:added', this.onProductAdded, options);
      this.addEventListener('click', this.onClick, options);

      var drawer = this.drawerElement();
      (drawer || document).addEventListener('keydown', this.onDrawerKeydownCapture, {
        capture: true,
        signal: this.listenerController.signal
      });

      // Server-rendered cart state: the first paint costs one request, and only
      // when the cart already has something in it. Rendering before the drawer
      // is opened also keeps the theme's focus trap accurate.
      var seedIds = numericList(data.cartProductIds);
      if (seedIds.length) {
        this.refreshFromCart({
          items: seedIds.map(function (id) {
            return { product_id: id };
          }),
          seed: Number(data.seedProductId) || seedIds[seedIds.length - 1]
        });
      }
    }

    disconnectedCallback() {
      this.generation += 1;
      this.bound = false;
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
      if (this.requestController) {
        this.requestController.abort();
        this.requestController = null;
      }
      if (this.listenerController) {
        this.listenerController.abort();
        this.listenerController = null;
      }
    }

    /* ------------------------------------------------------------- accessors */

    drawerElement() {
      return this.closest ? this.closest('.drawer') || document.getElementById('CartDrawer') : document.getElementById('CartDrawer');
    }

    hasContent() {
      return !!(this.bodyEl && !this.bodyEl.hasAttribute('hidden') && this.listEl && this.listEl.children.length);
    }

    /* ---------------------------------------------------------------- events */

    onCartEvent(event) {
      // Ignore the events this element broadcasts itself, otherwise every add
      // would run a second identical cart read.
      if (event && event.detail && event.detail.source === TAG) {
        return;
      }
      this.scheduleRefresh();
    }

    onProductAdded(event) {
      var product = event && event.detail ? event.detail.product : null;
      var id = product ? Number(product.product_id || product.id) : 0;
      if (isFinite(id) && id > 0) {
        this.lastAddedProductId = id;
      }
      if (event && event.detail && event.detail.source === TAG) {
        return;
      }
      this.scheduleRefresh();
    }

    onClick(event) {
      var target = event && event.target;
      var trigger = target && target.closest ? target.closest('[data-ktc-action="add"]') : null;
      if (!trigger || !this.contains(trigger)) {
        return;
      }
      event.preventDefault();
      this.addToCart(trigger);
    }

    /*
      The theme caches the drawer's focusable elements when the drawer opens, so
      controls rendered afterwards would be skipped by a forward Tab. This
      completes the loop without touching theme.js, and only while this block
      actually has content — with no recommendations the drawer behaves exactly
      as it does today.
    */
    onDrawerKeydownCapture(event) {
      if (!event || event.key !== 'Tab' || event.defaultPrevented) {
        return;
      }
      if (!this.hasContent()) {
        return;
      }
      var drawer = this.drawerElement();
      if (!drawer || !drawer.classList.contains('drawer--is-open')) {
        return;
      }
      if (!drawer.contains(event.target)) {
        return;
      }

      var focusables = [];
      var nodes = drawer.querySelectorAll(FOCUSABLE);
      for (var index = 0; index < nodes.length; index += 1) {
        if (!nodes[index].disabled && isVisible(nodes[index])) {
          focusables.push(nodes[index]);
        }
      }
      if (focusables.length < 2) {
        return;
      }

      var position = focusables.indexOf(event.target);
      if (position === -1) {
        return;
      }

      var forward = !event.shiftKey;
      var atEdge = forward ? position === focusables.length - 1 : position === 0;

      if (!atEdge) {
        // Natural tab order already reaches the next control; stop the theme's
        // stale cached trap from wrapping past this block.
        event.stopPropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      focusables[forward ? 0 : focusables.length - 1].focus();
    }

    /* -------------------------------------------------------------- refresh */

    scheduleRefresh() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }
      this.refreshTimer = setTimeout(function () {
        this.refreshTimer = null;
        this.refresh();
      }.bind(this), REFRESH_DEBOUNCE_MS);
    }

    async refresh() {
      var generation = this.beginRequest();
      var cart = await this.fetchJson(this.cartUrl).catch(function () {
        return null;
      });
      if (generation !== this.generation) {
        return;
      }
      if (!cart) {
        this.fail();
        return;
      }
      this.refreshFromCart(cart, generation);
    }

    async refreshFromCart(cart, existingGeneration) {
      var generation = existingGeneration || this.beginRequest();
      var items = (cart && Array.isArray(cart.items)) ? cart.items : [];

      if (!items.length) {
        this.clear();
        return;
      }

      var cartProductIds = new Set();
      items.forEach(function (item) {
        var id = Number(item && item.product_id);
        if (isFinite(id) && id > 0) {
          cartProductIds.add(id);
        }
      });

      var seed = this.pickSeed(items, cartProductIds, cart && cart.seed);
      if (!seed) {
        this.clear();
        return;
      }

      var requestLimit = Math.min(MAX_API_LIMIT, this.limit + cartProductIds.size + 1);
      var picked = [];
      var seen = new Set();
      var attempts = 0;
      var failures = 0;

      attempts += 1;
      var complementary = await this.recommendations('complementary', seed, requestLimit);
      if (generation !== this.generation) {
        return;
      }
      if (complementary === null) {
        failures += 1;
      } else {
        this.collect(complementary, picked, seen, cartProductIds);
      }

      if (picked.length < this.limit) {
        attempts += 1;
        var related = await this.recommendations('related', seed, requestLimit);
        if (generation !== this.generation) {
          return;
        }
        if (related === null) {
          failures += 1;
        } else {
          this.collect(related, picked, seen, cartProductIds);
        }
      }

      if (failures === attempts) {
        this.fail();
        return;
      }

      this.render(picked.slice(0, this.limit));
    }

    beginRequest() {
      this.generation += 1;
      if (this.requestController) {
        this.requestController.abort();
      }
      this.requestController = new AbortController();
      return this.generation;
    }

    pickSeed(items, cartProductIds, serverSeed) {
      if (this.lastAddedProductId && cartProductIds.has(this.lastAddedProductId)) {
        return this.lastAddedProductId;
      }
      var seed = Number(serverSeed);
      if (isFinite(seed) && seed > 0 && cartProductIds.has(seed)) {
        return seed;
      }
      for (var index = 0; index < items.length; index += 1) {
        var candidate = Number(items[index] && items[index].product_id);
        if (isFinite(candidate) && candidate > 0) {
          return candidate;
        }
      }
      return 0;
    }

    fetchJson(url) {
      var signal = this.requestController ? this.requestController.signal : undefined;
      return fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: signal
      }).then(function (response) {
        if (!response || !response.ok) {
          throw new Error('Request failed: ' + url);
        }
        return response.json();
      });
    }

    recommendations(intent, seed, limit) {
      var key = intent + '|' + seed + '|' + limit;
      if (this.cache.has(key)) {
        return Promise.resolve(this.cache.get(key));
      }
      var url = withParams(this.recommendationsUrl, {
        product_id: seed,
        limit: limit,
        intent: intent
      });
      var self = this;
      return this.fetchJson(url)
        .then(function (payload) {
          var products = (payload && Array.isArray(payload.products)) ? payload.products : [];
          self.cache.set(key, products);
          return products;
        })
        .catch(function () {
          // Not cached: a transient failure must not poison later refreshes.
          return null;
        });
    }

    collect(products, picked, seen, cartProductIds) {
      for (var index = 0; index < products.length; index += 1) {
        if (picked.length >= this.limit) {
          return;
        }
        var entry = this.toEntry(products[index], seen, cartProductIds);
        if (entry) {
          seen.add(entry.id);
          picked.push(entry);
        }
      }
    }

    toEntry(product, seen, cartProductIds) {
      if (!product) {
        return null;
      }
      var id = Number(product.id);
      if (!isFinite(id) || id <= 0 || seen.has(id) || cartProductIds.has(id)) {
        return null;
      }
      if (product.available !== true) {
        return null;
      }

      var variantId = null;
      if (Array.isArray(product.variants)) {
        var available = product.variants.filter(function (variant) {
          return variant && variant.id && variant.available !== false;
        });
        if (!available.length) {
          return null;
        }
        // One available variant is unambiguous, so it earns a one-tap Add.
        // Anything else goes to the product page rather than guessing.
        if (available.length === 1) {
          variantId = Number(available[0].id);
        }
      }

      var image = imageOf(product);
      var varies = product.price_varies === true;
      var price = varies && product.price_min !== undefined && product.price_min !== null
        ? product.price_min
        : (product.price !== undefined && product.price !== null ? product.price : product.price_min);

      return {
        id: id,
        title: String(product.title || ''),
        url: String(product.url || ''),
        price: price,
        priceVaries: varies,
        imageSrc: image.src,
        imageAlt: image.alt || String(product.title || ''),
        variantId: variantId
      };
    }

    /* ---------------------------------------------------------------- render */

    render(entries) {
      if (!entries.length) {
        this.clear();
        return;
      }

      this.hadFocus = !!(document.activeElement && this.contains(document.activeElement));

      var self = this;
      replaceChildren(this.listEl, entries.map(function (entry) {
        return self.card(entry);
      }));
      this.bodyEl.removeAttribute('hidden');

      if (!this.announcedOnce) {
        this.announce(fill(this.labels.available, '[count]', String(entries.length)));
        this.announcedOnce = true;
      }

      this.restoreFocus();
    }

    card(entry) {
      var item = element('li', {
        class: 'ktc-rec__item',
        'data-ktc-rec-item': '',
        'data-product-id': entry.id
      });

      var media = element('div', { class: 'ktc-rec__media' });
      if (entry.imageSrc) {
        media.appendChild(element('img', {
          src: sizedImage(entry.imageSrc, IMAGE_PIXELS),
          alt: entry.imageAlt,
          width: '56',
          height: '56',
          loading: 'lazy',
          decoding: 'async'
        }));
      }
      item.appendChild(media);

      var details = element('div', { class: 'ktc-rec__details' });
      details.appendChild(entry.url
        ? element('a', { class: 'ktc-rec__name', href: entry.url }, entry.title)
        : element('span', { class: 'ktc-rec__name' }, entry.title));

      var money = formatMoney(entry.price, this.moneyFormat);
      details.appendChild(element('span', {
        class: 'ktc-rec__price'
      }, entry.priceVaries ? this.labels.from + ' ' + money : money));
      item.appendChild(details);

      if (entry.variantId) {
        item.appendChild(element('button', {
          type: 'button',
          class: 'ktc-rec__action',
          'data-ktc-action': 'add',
          'data-product-id': entry.id,
          'data-variant-id': entry.variantId,
          'data-product-title': entry.title,
          'aria-label': fill(this.labels.addAria, '[title]', entry.title)
        }, this.labels.add));
      } else {
        item.appendChild(element('a', {
          class: 'ktc-rec__action',
          href: entry.url,
          'data-ktc-action': 'options',
          'data-product-id': entry.id,
          'aria-label': fill(this.labels.optionsAria, '[title]', entry.title)
        }, this.labels.options));
      }

      return item;
    }

    clear() {
      this.hadFocus = !!(document.activeElement && this.contains(document.activeElement));
      replaceChildren(this.listEl, []);
      this.bodyEl.setAttribute('hidden', '');
      if (this.hadFocus) {
        var form = document.getElementById('CartDrawerForm');
        var closeButton = form ? form.querySelector('.js-drawer-close') : null;
        var fallback = closeButton || form;
        if (fallback && typeof fallback.focus === 'function') {
          fallback.focus();
        }
      }
      this.hadFocus = false;
    }

    /*
      A failed lookup leaves whatever is already on screen alone. The cart keeps
      its size, its controls and its checkout; only the live region speaks.
    */
    fail() {
      this.announce(this.labels.unavailable);
    }

    announce(message) {
      if (this.statusEl) {
        this.statusEl.textContent = String(message || '');
      }
    }

    restoreFocus() {
      var wanted = this.restoreProductId;
      this.restoreProductId = null;

      if (!this.hadFocus) {
        return;
      }
      this.hadFocus = false;

      var target = null;
      if (wanted) {
        target = this.listEl.querySelector('[data-ktc-action][data-product-id="' + wanted + '"]');
      }
      if (!target) {
        target = this.listEl.querySelector('[data-ktc-action]');
      }
      if (!target) {
        target = this.titleEl;
      }
      if (!target) {
        target = document.getElementById('CartDrawerForm');
      }
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    }

    /* ------------------------------------------------------------ add to cart */

    clearCardError(button) {
      var item = button.closest ? button.closest('[data-ktc-rec-item]') : null;
      if (!item) {
        return;
      }
      var existing = item.querySelector('[data-ktc-rec-error]');
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
      }
    }

    showCardError(button, message) {
      var item = button.closest ? button.closest('[data-ktc-rec-item]') : null;
      if (!item) {
        return;
      }
      this.clearCardError(button);
      item.appendChild(element('p', {
        class: 'ktc-rec__error',
        'data-ktc-rec-error': ''
      }, message));
    }

    async addToCart(button) {
      if (button.getAttribute('aria-busy') === 'true') {
        return;
      }

      var variantId = Number(button.getAttribute('data-variant-id'));
      if (!isFinite(variantId) || variantId <= 0) {
        return;
      }

      var productId = Number(button.getAttribute('data-product-id')) || null;
      var title = button.getAttribute('data-product-title') || '';
      var self = this;
      var restoreButtonFocus = document.activeElement === button;

      this.clearCardError(button);
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', '');
      button.disabled = true;

      var released = function () {
        button.removeAttribute('aria-busy');
        button.removeAttribute('disabled');
        button.disabled = false;
        if (restoreButtonFocus && self.contains(button) && typeof button.focus === 'function') {
          button.focus();
        }
      };

      var payload = null;
      var response = null;

      try {
        response = await fetch(this.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        payload = await response.json().catch(function () {
          return null;
        });
      } catch (error) {
        released();
        this.showCardError(button, this.labels.addFailed);
        this.announce(this.labels.addFailed);
        return;
      }

      var rejected = !response.ok || (payload && payload.status && Number(payload.status) >= 400);
      if (rejected) {
        released();
        var message = (payload && (payload.description || payload.message)) || this.labels.addFailed;
        this.showCardError(button, message);
        this.announce(message);
        return;
      }

      released();
      button.classList.add('is-added');
      this.lastAddedProductId = productId;
      this.restoreProductId = productId;
      this.announce(fill(this.labels.added, '[title]', title));

      /*
        Hand the cart back to the theme the quiet way. `cart:build` is theme.js's
        own rebuild hook: CartForm re-reads /cart?view=ajax and refreshes items,
        discounts, subtotal and the bubble count.

        `ajaxProduct:added` is deliberately not re-broadcast. CartDrawer's
        listener calls open(), which re-runs theme.a11y.trapFocus() on every add
        — that moves focus off the button the shopper just pressed and stacks a
        second focusin/focusout pair on documentElement each time, because
        theme.delegate.on overwrites its namespace record while still adding the
        listener. A namespaced event carries the same payload without either
        side effect.
      */
      var addedLine = payload && Array.isArray(payload.items) && payload.items.length
        ? payload.items[0]
        : payload;

      var cart = await this.fetchCartForBroadcast();
      document.dispatchEvent(new CustomEvent('cart:build', { detail: { source: TAG } }));
      if (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cart, source: TAG }
        }));
        document.dispatchEvent(new CustomEvent('ktc:recommendation:added', {
          detail: { product: addedLine, variantId: variantId, cart: cart, source: TAG }
        }));
        this.refreshFromCart(cart);
      } else {
        this.scheduleRefresh();
      }
    }

    fetchCartForBroadcast() {
      var self = this;
      return fetch(this.cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response || !response.ok) {
            throw new Error('cart read failed');
          }
          return response.json();
        })
        .catch(function () {
          return null;
        })
        .then(function (cart) {
          if (cart) {
            self.beginRequest();
          }
          return cart;
        });
    }
  }

  customElements.define(TAG, KtcCartRecommendations);

  if (typeof window !== 'undefined') {
    window.KtcCartRecommendations = KtcCartRecommendations;
  }
})();
